import { useState } from "react";
import { ActionHandler, ModalPanel, ToolBox } from "@/components/UI/tools";
import { Card } from "@/app/workspace/settings/ui";
import { ArrowDown, Close } from "@/components/UI/svgs";
import { GallerySection, Placeholder, Specimen, Stage, noop } from "../galleryKit";

const FoldDemo = () => {
    const [open, setOpen] = useState(true);
    return (
        <div className="fold w-[17rem]">
            <div className="fold-header" onClick={() => setOpen(!open)}>
                <span className={`fold-chevron ${open ? "" : "-rotate-90"}`}><ArrowDown size={16} /></span>
                <span className="fold-title">Fold title</span>
            </div>
            {open && <div className="fold-body flex flex-col gap-[.35rem]"><Placeholder /><Placeholder /></div>}
        </div>
    );
};

const SplitDemo = () => (
    <Stage height="19rem">
        <div className="split-layout !pt-1">
            <div className="split-frame !pl-1">
                <div className="split-sidebar !w-[10rem] pr-1"><Placeholder label="sidebar" className="h-full" /></div>
                <div className="split-main px-1">
                    <div className="inspector-header mb-1">
                        <p className="eyebrow">Eyebrow</p>
                        <p className="h2 truncate">Inspector title</p>
                    </div>
                    <Placeholder label="inspector" className="h-[5rem]" />
                    <div className="inspector-empty">
                        <p className="inspector-empty-title">Nothing selected</p>
                        <p className="inspector-empty-text">Pick an item in the sidebar.</p>
                    </div>
                </div>
            </div>
        </div>
    </Stage>
);

const ModalDemo = () => (
    <Stage height="20rem">
        <div className="modal !h-full">
            <div className="modal-backdrop modal-backdrop-dismissable" />
            <div className="modal-frame !h-full">
                <ModalPanel
                    title="Modal title"
                    close={<ActionHandler funq={noop}>×</ActionHandler>}
                    footer={<><ActionHandler funq={noop}>Cancel</ActionHandler><ActionHandler funq={noop}>Proceed</ActionHandler></>}
                >
                    <div className="modal-content"><Placeholder label="body" className="h-[5rem]" /></div>
                </ModalPanel>
            </div>
        </div>
    </Stage>
);

const SheetDemo = () => (
    <Stage height="12rem">
        <div className="sheet !h-full">
            <div className="sheet-backdrop" />
            <div className="sheet-body !h-full !max-h-full !w-full p-1">
                <div className="panel-elevated w-full p-1"><Placeholder label="sheet content" className="h-full" /></div>
            </div>
        </div>
    </Stage>
);

export const SurfacesSection = () => (
    <GallerySection id="surfaces" title="Containers" intro="Surfaces that hold other content. Shown here with placeholder content; their job is framing, not data.">
        <Specimen
            name="Card"
            classes={["card"]}
            usage="Bordered group without padding: its rows or sections pad themselves. Use Card from settings/ui."
            demo={() => <Card><div className="p-1"><Placeholder /></div></Card>}
        />
        <Specimen
            name="Panel"
            classes={["panel", "panel-header", "panel-elevated"]}
            usage="Padded, bordered block for free-form content (forms, admin panes). panel-header is a flush header strip; panel-elevated floats on canvas without a border."
            rules={["Stack the panel's children with its built-in gap; don't add margins between them."]}
            code={`<div className="panel"><Field label="…">…</Field>…</div>`}
            demo={() => (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
                    <div className="panel"><Placeholder /><Placeholder /></div>
                    <div className="panel !p-0 !gap-0 overflow-hidden">
                        <div className="panel-header">panel-header</div>
                        <pre className="code-block rounded-none">output…</pre>
                    </div>
                    <div className="panel-elevated p-1"><Placeholder /></div>
                </div>
            )}
        />
        <Specimen
            name="Tool box"
            classes={["toolbox", "toolbox-body", "toolbox-body-interactive", "toolbox-body-user", "toolbox-body-lift", "toolbox-body-draft", "toolbox-compact", "toolbox-compact-content", "toolbox-intent"]}
            usage="Recessed block for tool calls and system output in a chat, and dashboard widgets. Rendered by ToolBox; is_small gives the compact status line."
            demo={() => (
                <div className="flex flex-col max-w-[32rem]">
                    <ToolBox is_small intent="Intent">Compact status line</ToolBox>
                    <ToolBox is_small={false} intent="Intent"><div className="p-1 w-full"><Placeholder /></div></ToolBox>
                    <ToolBox is_small={false} user lift onClick={noop}><div className="p-1 w-full"><Placeholder label="interactive (hover)" /></div></ToolBox>
                    <ToolBox is_small={false} draft><div className="p-1 w-full"><Placeholder label="draft" /></div></ToolBox>
                </div>
            )}
        />
        <Specimen
            name="Modal"
            classes={["modal", "modal-backdrop", "modal-backdrop-dismissable", "modal-frame", "modal-panel", "modal-header", "modal-title", "modal-body", "modal-content", "modal-footer"]}
            usage="Opened with appState.setModal / appendModal; never render your own overlay. ModalPanel is the visual shell if you need one inline."
            rules={["Bodies that need custom close behaviour register it with useModalClose instead of drawing their own × button."]}
            code={`appState.setModal({ title: "Delete?", body: "This cannot be undone.", action: remove })`}
            demo={() => <ModalDemo />}
        />
        <Specimen
            name="Fold"
            classes={["fold", "fold-header", "fold-chevron", "fold-title", "fold-body"]}
            usage="Collapsible sidebar section with a sticky header."
            demo={() => <FoldDemo />}
        />
        <Specimen
            name="Split layout and inspector header"
            classes={["split-layout", "split-frame", "split-sidebar", "split-main", "inspector-header", "eyebrow", "inspector-empty", "inspector-empty-title", "inspector-empty-text"]}
            usage="Sidebar plus main column (settings). The inspector header stacks an eyebrow over the title."
            demo={() => <SplitDemo />}
        />
        <Specimen
            name="Sheet"
            classes={["sheet", "sheet-backdrop", "sheet-body"]}
            usage="Full-screen overlay used on mobile for the object editor."
            demo={() => <SheetDemo />}
        />
        <Specimen
            name="Page"
            classes={["page", "page-constrained", "scroll-box", "scroll-box-reverse", "scrollbar-none", "scrollbar-left", "scrollbar-left-item"]}
            usage="PageBox wraps list and entry pages: a scroll-box with a constrained page column. scroll-box-reverse keeps content pinned to the bottom (chat)."
            demo={() => (
                <Stage height="9rem">
                    <div className="scroll-box">
                        <div className="page page-constrained !mt-1 !pl-1">
                            <Placeholder label="page content" className="h-[12rem]" />
                        </div>
                    </div>
                </Stage>
            )}
        />
        <Specimen
            name="Close affordance"
            classes={["btn", "btn-ghost"]}
            usage="Panels close with a ghost icon button in their top-right corner."
            demo={() => (
                <div className="panel max-w-[22rem]">
                    <div className="flex flex-row items-center justify-between">
                        <p className="hsub">Panel title</p>
                        <ActionHandler funq={noop} className="btn-ghost" title="Close"><Close /></ActionHandler>
                    </div>
                    <Placeholder />
                </div>
            )}
        />
    </GallerySection>
);
